import * as ko from "knockout";
import type { Lang, Localized, Section, LocalizedTemplate, RootVm, EmailTemplateVM, WhatsAppTarget } from "../types";

declare const notyf: any;

// Locale-aware conjunction for joining a list of names ("a, b und c").
const CONJ: Record<Lang, string> = { de: "und", en: "and", fr: "et" };

// One clause per reaction group, plus the shared support-offer closing.
// Verb agrees with the number of artists (plural) where the language needs it.
const REACTION_COPY: Record<Lang, {
    no: (names: string, plural: boolean) => string;
    approve: (names: string, plural: boolean) => string;
    counter_offer: (names: string, plural: boolean) => string;
    replied_in_chat: (names: string, plural: boolean) => string;
    not_reacted: (names: string, plural: boolean) => string;
    closing: string;
}> = {
    de: {
        no: (n, p) => `*${n}* ${p ? "haben" : "hat"} leider abgesagt.`,
        approve: (n, p) => `*${n}* ${p ? "haben" : "hat"} dein Angebot angenommen.`,
        counter_offer: (n, p) => `*${n}* ${p ? "haben" : "hat"} dir ein Gegenangebot gemacht.`,
        replied_in_chat: (n, p) => `*${n}* ${p ? "haben" : "hat"} dir eine Nachricht geschickt.`,
        not_reacted: (n, p) => `*${n}* ${p ? "haben" : "hat"} sich noch nicht zurückgemeldet.`,
        closing: `Ich wüsste gerne, ob ich dich bei der Anfrage unterstützen kann.`,
    },
    en: {
        no: (n) => `*${n}* unfortunately declined.`,
        approve: (n) => `*${n}* accepted your offer.`,
        counter_offer: (n) => `*${n}* made you a counter-offer.`,
        replied_in_chat: (n) => `*${n}* sent you a message.`,
        not_reacted: (n, p) => `*${n}* ${p ? "haven't" : "hasn't"} responded yet.`,
        closing: `I'd love to know whether I can support you with your enquiry.`,
    },
    fr: {
        no: (n, p) => `*${n}* ${p ? "ont" : "a"} malheureusement décliné.`,
        approve: (n, p) => `*${n}* ${p ? "ont" : "a"} accepté votre offre.`,
        counter_offer: (n, p) => `*${n}* ${p ? "ont" : "a"} fait une contre-proposition.`,
        replied_in_chat: (n, p) => `*${n}* vous ${p ? "ont" : "a"} envoyé un message.`,
        not_reacted: (n, p) => `*${n}* n'${p ? "ont" : "a"} pas encore répondu.`,
        closing: `J'aimerais savoir si je peux vous aider pour votre demande.`,
    },
};

export type HostReminderParams = {
    rootvm: RootVm;
};

export class HostReminderTemplateVM implements EmailTemplateVM {
    type = "HostReminderTemplate";
    rootvm: RootVm;

    language = ko.observable<Lang>("de");

    subject = ko.observable<string>("");
    hi = ko.observable<string>("");
    greetings = ko.observable<string>("");
    subject_template = ko.observable<Localized>({ de: "", en: "", fr: "" });
    hi_template = ko.observable<Localized>({ de: "", en: "", fr: "" });
    greetings_template = ko.observable<Localized>({ de: "", en: "", fr: "" });

    sections: ko.ObservableArray<Section>;

    person_uuid = ko.observable<string>("");
    // First invite's event_uuid from the combined event, logged to email_sent_log.
    event_uuid = ko.observable<string>("");
    host_name = ko.observable<string>("");
    host_phone = ko.observable<string>("");
    display_name = ko.observable<string>("");
    // Artist names grouped by how they reacted to the host's invites. Derived
    // from the SCCombinedEvent in set_send_host_reminder.
    artists_no = ko.observableArray<string>([]);              // declined
    artists_approve = ko.observableArray<string>([]);         // accepted the offer
    artists_counter_offer = ko.observableArray<string>([]);   // made a counter-offer
    artists_replied_in_chat = ko.observableArray<string>([]); // invite state, but replied in chat
    artists_not_reacted = ko.observableArray<string>([]);     // no response at all
    // Per-group include toggles for the "Artist reaction" section.
    include_no = ko.observable<boolean>(true);
    include_approve = ko.observable<boolean>(true);
    include_counter_offer = ko.observable<boolean>(true);
    include_replied_in_chat = ko.observable<boolean>(true);
    include_not_reacted = ko.observable<boolean>(true);
    // Section 2 toggle: true -> "tried to call, couldn't reach you", false ->
    // this mail is the first contact. Defaults to false when host has no phone.
    tried_phone = ko.observable<boolean>(true);

    constructor(params: HostReminderParams) {
        this.rootvm = params.rootvm;

        this.sections = ko.observableArray<Section>([
            this.makeSection("Thanks", 1),
            this.makeSection("Couldn't reach you", 1, "tried_phone"),
            this.makeSection("Artist reaction", 5, "artist_reaction"),
            this.makeSection("Offer support", 1),
        ]);

        this.language.subscribe((lang) => {
            if (lang) this.populate(lang);
        });
        this.tried_phone.subscribe(() => this.populate());
        this.include_no.subscribe(() => this.populate());
        this.include_approve.subscribe(() => this.populate());
        this.include_counter_offer.subscribe(() => this.populate());
        this.include_replied_in_chat.subscribe(() => this.populate());
        this.include_not_reacted.subscribe(() => this.populate());
    }

    private makeSection(label: string, rows: number, id?: string): Section {
        return {
            label,
            value: ko.observable<string>(""),
            template: ko.observable<Localized>({ de: "", en: "", fr: "" }),
            rows,
            id,
        };
    }

    // Joins names with locale-aware conjunction:
    // 1 -> "a", 2 -> "a und b", 3+ -> "a, b und c".
    private joinNames(names: string[], conjunction: string): string {
        if (names.length <= 1) return names[0] ?? "";
        return `${names.slice(0, -1).join(", ")} ${conjunction} ${names[names.length - 1]}`;
    }

    // Builds the "Artist reaction" section: one clause per enabled, non-empty
    // reaction group, followed by the shared support-offer closing line.
    private buildReactionSection(lang: Lang): string {
        const copy = REACTION_COPY[lang];
        const clause = (
            names: string[],
            include: boolean,
            make: (n: string, plural: boolean) => string,
        ) => (include && names.length
            ? make(this.joinNames(names, CONJ[lang]), names.length > 1)
            : "");

        const clauses = [
            clause(this.artists_approve(), this.include_approve(), copy.approve),
            clause(this.artists_counter_offer(), this.include_counter_offer(), copy.counter_offer),
            clause(this.artists_no(), this.include_no(), copy.no),
            clause(this.artists_replied_in_chat(), this.include_replied_in_chat(), copy.replied_in_chat),
            clause(this.artists_not_reacted(), this.include_not_reacted(), copy.not_reacted),
        ].filter(Boolean);

        return clauses.length ? `${clauses.join("\n")}\n\n${copy.closing}` : copy.closing;
    }

    private buildTemplate(): LocalizedTemplate {
        const host = this.host_name();
        const display = this.display_name();

        // Section 2 – either "tried to call you" or "mail as first contact".
        const phoneLineDe = this.tried_phone()
            ? `Ich habe dich leider telefonisch nicht erreicht und melde mich daher einmal auf diesem Weg.`
            : `Ich melde mich einmal direkt auf diesem Weg bei dir.`;
        const phoneLineEn = this.tried_phone()
            ? `Unfortunately I couldn't reach you by phone, so I'm getting in touch this way instead.`
            : `I'm reaching out to you directly this way.`;
        const phoneLineFr = this.tried_phone()
            ? `Malheureusement je n'ai pas réussi à vous joindre par téléphone, je vous écris donc par ce biais.`
            : `Je vous écris directement par ce biais.`;

        return {
            subject: {
                de: `Deine Anfrage bei SofaConcerts`,
                en: `Your enquiry at SofaConcerts`,
                fr: `Votre demande chez SofaConcerts`,
            },
            hi: {
                de: `Hallo ${host},`,
                en: `Hello ${host},`,
                fr: `Bonjour ${host},`,
            },
            sections: [
                {
                    de: `danke für deine Anfrage bei SofaConcerts! 💛`,
                    en: `thank you for your enquiry at SofaConcerts! 💛`,
                    fr: `merci pour votre demande chez SofaConcerts ! 💛`,
                },
                {
                    de: phoneLineDe,
                    en: phoneLineEn,
                    fr: phoneLineFr,
                },
                {
                    de: this.buildReactionSection("de"),
                    en: this.buildReactionSection("en"),
                    fr: this.buildReactionSection("fr"),
                },
                {
                    de: `Bei Fragen rund um das Thema Buchung oder Buchungsablauf, melde dich gerne bei mir.`,
                    en: `If you have any questions about booking or how the booking process works, feel free to reach out to me.`,
                    fr: `Pour toute question concernant la réservation ou son déroulement, n'hésitez pas à me contacter.`,
                },
            ],
            greetings: {
                de: `Viele Grüße,\n*${display}*`,
                en: `Best regards,\n*${display}*`,
                fr: `Cordialement,\n*${display}*`,
            },
        };
    }

    populate = (language: Lang = this.language()) => {
        const tpl = this.buildTemplate();

        this.subject_template(tpl.subject);
        this.hi_template(tpl.hi);
        this.greetings_template(tpl.greetings);

        tpl.sections.forEach((localized, i) => {
            const section = this.sections()[i];
            if (!section) return;
            section.template(localized);
            section.value(localized[language] ?? "");
        });

        this.subject(tpl.subject[language] ?? "");
        this.hi(tpl.hi[language] ?? "");
        this.greetings(tpl.greetings[language] ?? "");
    };

    whatsappTarget = (): WhatsAppTarget | null => {
        const phone = this.host_phone();
        if (!phone) return null;
        const text = [
            this.hi(),
            ...this.sections().map((s) => s.value()),
            this.greetings(),
        ].filter(Boolean).join("\n\n");
        return { phone, text };
    };

    send_mail = () => {
        const data = {
            subject: this.subject(),
            hi: this.hi(),
            sections: this.sections().map((s) => s.value()),
            greetings: this.greetings(),
            person_uuid: this.person_uuid(),
            event_uuid: this.event_uuid(),
            locale: this.language(),
        };
        return fetch("/api/admin/host/send-reminder", {
            method: "POST",
            body: JSON.stringify(data),
        })
            .then((r) => r.json())
            .then(() => notyf.success("Reminder sent."));
    };
}
